import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import endpoints from '../../../../../../api/endpoints';
import { useFetch } from '../../../../../../api/useFetch';
import CommonReports from '../../../../../common/reports';
import { downloadReport } from '../../../../../common/reports/utils/downloadReport';
import { HandleDownloadReport } from '../../../../../common/reports/actions/interfaces';
import { getPermissionToGenerateReports } from '../../../../../common/reports/utils/mapReportToTableStructure';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import { FiltersFormValues, FiltersInitialData, ReportsData } from '../../../../../common/reports/interfaces';

import locales from './locales';
import FiltersForm from './filters/filtersForm';
import FilterDetails from './filters/filterDetails';
import FiltersSummary from './filters/filtersSummary';
import { mapTableDataIntoApiFormat } from './dataTransformator';
import { CustomerEnrollmentReportCreateForm, CustomerEnrollmentReportDetailsMetadataResponse } from './interfaces';
import { filtersValidationSchema } from './filters/filtersValidation';

const initFilters = {
  name: '',
  statuses: [],
  operationIds: [],
  customerMsisdns: [],
  providerNames: [],
  from: null,
  to: null
};

const Reports = () => {
  const { data = {} as ReportsData, loading, refresh, error } = useFetch<ReportsData>({
    pathname: endpoints.customerEnrollmentReports
  });

  const handleDeleteReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.customerEnrollmentReports}/${id}`, {
      method: 'delete'
    });
  const handleCancelReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.customerEnrollmentReports}/${id}`, {
      method: 'delete'
    });

  const handleRetryReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.customerEnrollmentReportsRetry(id)}`, {
      method: 'POST'
    });

  const handleRegenerateReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.customerEnrollmentReportsRegenerate(id)}`, {
      method: 'POST'
    });

  const handleGenerateReportRequest = (values: CustomerEnrollmentReportCreateForm) =>
    fetchWithTokenRefresh(endpoints.customerEnrollmentReports, addParams('POST', mapTableDataIntoApiFormat(values)));

  const handleDownloadReport: HandleDownloadReport = (id, name, size) => {
    downloadReport(id, `${name}.csv`, 'customerEnrollment', size);
  };

  const location = useLocation<{ filters: FiltersInitialData } | undefined>();

  const [initialValues, setInitialValues] = useState(location.state?.filters || initFilters);

  return (
    <CommonReports
      error={error}
      filters={{
        form: (filtersFormValues: FiltersFormValues<CustomerEnrollmentReportCreateForm>) => (
          <FiltersForm {...filtersFormValues} />
        ),
        validation: filtersValidationSchema,
        details: (metadata: CustomerEnrollmentReportDetailsMetadataResponse) => <FilterDetails filters={metadata} />,
        summary: (metadata: CustomerEnrollmentReportDetailsMetadataResponse) => <FiltersSummary filters={metadata} />,
        initialValues,
        clearValues: () => {
          setInitialValues(initFilters);
        },
        predefinedReportGeneration: Boolean(location.state?.filters)
      }}
      locales={locales}
      data={data}
      emptyMsg="You don't have any Customer Enrollment reports to display"
      loading={loading}
      refresh={refresh}
      handleDeleteReportRequest={handleDeleteReportRequest}
      handleCancelReportRequest={handleCancelReportRequest}
      handleRetryReportRequest={handleRetryReportRequest}
      handleRegenerateReportRequest={handleRegenerateReportRequest}
      handleGenerateReportRequest={handleGenerateReportRequest}
      handleDownloadReport={handleDownloadReport}
      generationAllowed={getPermissionToGenerateReports(data.limits?.status)}
    />
  );
};

export default Reports;
